import api from 'api';
import { FALLBACK_TAX_RATE } from 'constants/pricing';

// There is no local copy of the quiz: an empty/unreachable /api/quiz/config
// means no quiz, and `source` lets QuizPage say so instead of spinning.
export async function fetchQuizConfig() {
  try {
    const { data } = await api.get('/quiz/config');
    if (data.steps?.length) return { steps: data.steps, source: 'live' };
    return { steps: [], source: 'unavailable' };
  } catch {
    return { steps: [], source: 'unavailable' };
  }
}

export async function searchNutritionists(q) {
  try {
    const { data } = await api.get('/quiz/nutritionists', { params: { q } });
    return data.results || [];
  } catch {
    return [];
  }
}

export async function submitQuiz(answers) {
  try {
    const { data } = await api.post('/quiz/submit', { answers });
    return data;
  } catch {
    const cal = answers.goal === 'muscle-gain' ? 2400 : answers.goal === 'weight-loss' ? 1700 : 2000;
    const profile = {
      goal: answers.goal || 'habits',
      calories: cal,
      proteinG: answers.goal === 'muscle-gain' ? 150 : 100,
      carbsG: Math.round(cal * 0.45 / 4),
      fatG: Math.round(cal * 0.30 / 9),
      fiberG: 25,
      restrictions: (answers.restrictions || []).filter((r) => r !== 'none'),
      zip: answers.zip || '',
    };
    return {
      success: true,
      clientRowIds: [],
      hasFullQuiz: true,
      profile,
      summary: `About ${profile.calories} cal/day`,
    };
  }
}

// Writes edited targets back to the Client rows the quiz just created.
export async function saveQuizTargets(rowIds, meals) {
  const { data } = await api.patch('/quiz/nutrition', { rowIds, meals });
  if (!data?.success) throw new Error(data?.error || 'Could not save.');
  return data;
}

// Same rows as saveQuizTargets; the server also covers the person's other rows.
// With `meal`, only that meal's rows change.
export async function saveQuizRestrictions(rowIds, restrictionIds, meal) {
  const { data } = await api.patch('/quiz/restrictions', { rowIds, restrictionIds, ...(meal ? { meal } : {}) });
  if (!data?.success) throw new Error(data?.error || 'Could not save.');
  return data;
}

export async function fetchMenu({ personalized = false } = {}) {
  try {
    const { data } = await api.get('/menu', {
      params: personalized ? { personalized: 'true' } : {},
    });
    return {
      dishes: data.dishes || [],
      deliveryWeek: data.deliveryWeek || null,
      defaultMealTargets: data.defaultMealTargets || {},
    };
  } catch {
    return { dishes: [], deliveryWeek: null, defaultMealTargets: {} };
  }
}

/**
 * Checks whether a zip is in the service area and, if so, returns the
 * delivery day/time/fee combos actually available for it (`slots`), so the
 * checkout form can restrict its delivery picker instead of offering a
 * static list. Returns null if the backend can't be reached at all.
 */
export async function checkDeliveryAvailability(zip) {
  try {
    const { data } = await api.get(`/delivery-availability/${encodeURIComponent(zip)}`);
    return data;
  } catch {
    return null;
  }
}

/** Weekly subscription day/time slots for a zip (no specific date), as { supported, message, slots }; null if unreachable. */
export async function fetchSubscriptionDeliveryOptions(zip) {
  try {
    const { data } = await api.get(`/subscription-delivery-options/${encodeURIComponent(zip)}`);
    const slots = (data.days || []).flatMap((d) => d.slots.map((o) => ({ day: d.day, time: o.time, fee: o.cost })));
    return { supported: data.supported, message: data.message, slots };
  } catch {
    return null;
  }
}

/**
 * Read-only preview so the summary can show "Waived" before submitting.
 * Returns null (not an error) if unreachable, so the caller just shows the normal fee.
 */
export async function previewDeliveryFeeWaivers({ address, address2, city, state, zip, slots }) {
  try {
    const { data } = await api.post('/checkout/delivery-fee-preview', { address, address2, city, state, zip, slots });
    return data?.slots || null;
  } catch {
    return null;
  }
}

/**
 * Creates the guest order and a Stripe Checkout session for it.
 * Returns { checkoutUrl } to redirect to on success; falls back to a local
 * demo confirmation (no Stripe redirect) if the backend is unreachable.
 */
export async function submitGuestCheckout(payload) {
  try {
    const { data } = await api.post('/checkout/one-time-order', payload);
    return data;
  } catch (err) {
    if (err.response) {
      // Backend reached and rejected the request (e.g. unsupported zip) — surface it as-is.
      return err.response.data || { success: false, error: 'checkout_failed' };
    }
    // Backend unreachable — local dev fallback demo.
    const subtotal = payload.cart.reduce(
      (sum, line) => sum + (line.price || 0) * (line.quantity || 1),
      0,
    );
    const tax = subtotal * FALLBACK_TAX_RATE;
    const deliveryFee = 9.99;
    const total = subtotal + tax + deliveryFee;
    const orderId = `local_${Date.now().toString(36)}`;
    return {
      success: true,
      orderId,
      checkoutUrl: null,
      total: Math.round(total * 100) / 100,
      showOptionalQuiz: payload.entryFlow === 'menu-first' && !payload.hasFullQuiz,
      _local: true,
    };
  }
}

/**
 * Creates the pending Subscriptions rows (one per person/meal type) and a
 * Stripe `setup` Checkout Session to save a card for them. No charge happens
 * here — see backend/routes/subscriptions.js. Returns { success, checkoutUrl }
 * (or { success: false, error }) straight from the backend; unlike
 * submitGuestCheckout, there's no local fallback demo — a subscription
 * without a real saved card isn't a meaningful thing to fake.
 */
export async function submitSubscriptionCheckout(payload) {
  try {
    const { data } = await api.post('/subscriptions/checkout', payload);
    return data;
  } catch (err) {
    return err.response?.data || { success: false, error: 'subscription_checkout_failed' };
  }
}

export async function fetchConfirmation(orderId) {
  try {
    const { data } = await api.get(`/checkout/confirmation/${orderId}`);
    return data;
  } catch {
    return null;
  }
}

// Mirrors mealPlanService.checkPendingStatus — polled after the Stripe redirect
// until the webhook has finished applying the order.
export async function checkGuestOrderStatus(orderId) {
  const { data } = await api.get(`/checkout/one-time-order/${orderId}/status`);
  return data;
}

/** Newsletter signup — backend forwards to Mailchimp (or captures it until keys are set). */
export async function subscribeNewsletter(email, source = 'footer') {
  try {
    const { data } = await api.post('/newsletter/subscribe', { email, source });
    return data?.success ? { success: true } : { success: false, error: data?.error };
  } catch (err) {
    return { success: false, error: err.response?.data?.error || 'Could not subscribe. Please try again.' };
  }
}

/** Contact form submission — backend emails team@getsquarefare.com (or captures it). */
export async function submitContactForm(payload) {
  try {
    const { data } = await api.post('/contact', payload);
    return data?.success ? { success: true } : { success: false, error: data?.error };
  } catch (err) {
    return { success: false, error: err.response?.data?.error || 'Could not send your message. Please try again.' };
  }
}

/** Invite a household member to take the quiz (adds them to the invite list). */
export async function sendQuizInvite({ email, name, invitedBy }) {
  try {
    const { data } = await api.post('/quiz-invite', { email, name, invitedBy });
    return data?.success ? { success: true } : { success: false, error: data?.error };
  } catch (err) {
    return { success: false, error: err.response?.data?.error || 'Could not send the invite.' };
  }
}
